<template>
  <div v-if="productData" class="w-full space-y-12 lg:space-y-20">
    <!-- Hero Detail -->
    <SectionsHeroDetail
      :key="'heroDetail'"
      :title="heroTitle"
      :description="heroDescription"
      :media="productData?.heroDetail?.media"
      :backgroundColor="productData?.heroDetail?.backgroundColor"
      :color="productData?.heroDetail?.color"
      :breadcrumbs="heroBreadcrumbs"
    />
    <!-- Hero Detail -->

    <!-- Filter Menu & Content -->
    <section class="w-full">
      <div class="container">
        <div class="flex flex-col lg:flex-row gap-4 2xl:gap-8">
          <!-- Filter Menu (desktop) -->
          <div v-if="!isMobile" :class="filterMenuClass">
            <UiSideBarMenuSideMenuSkeleton v-if="categoriesPending" />
            <UiSideBarMenuSideMenu
              v-else
              :data="panelMenuItems"
              :expandedKeys="panelExpandedKeys"
            />
            <!-- <UiSideBarMenuSideBar /> -->
          </div>
          <!-- Filter Menu (desktop) -->

          <!-- Filter Menu (mobile) -->
          <Dialog
            v-if="isMobile"
            v-model:visible="visible"
            modal
            :header="t('Products.Filter')"
            :style="{ width: '50rem' }"
            :breakpoints="dialogBreakpoints"
          >
            <div class="w-full flex gap-2 flex-col">
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-black">
                  {{
                    t("Products.ModelsShown", {
                      count: totalProductCount,
                    })
                  }}
                </span>
                <button
                  v-if="canClearFilters"
                  :class="clearFilterButtonClass"
                  type="button"
                  @click="clearCategoryFilters"
                >
                  {{ t("Products.ClearFilters") }}
                </button>
              </div>
              <UiSideBarMenuSideMenuSkeleton v-if="categoriesPending" />
              <UiSideBarMenuSideMenu
                v-else
                :data="panelMenuItems"
                :expandedKeys="panelExpandedKeys"
              />
              <!-- <UiSideBarMenuSideBar /> -->
            </div>
          </Dialog>
          <!-- Filter Menu (mobile) -->

          <!-- Content -->
          <section :class="contentClass">
            <div
              class="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3"
            >
              <div class="flex flex-col w-full gap-3">
                <div
                  class="flex flex-wrap items-center justify-between gap-3 w-full"
                >
                  <div
                    v-if="!isMobile"
                    class="flex items-center gap-4 text-sm font-medium"
                  >
                    <span class="text-[13px] md:text-[14px] font-bold text-gray-800 tracking-wide">
                      {{
                        t("Products.ModelsShown", {
                          count: totalProductCount,
                        })
                      }}
                    </span>
                    <button
                      v-if="canClearFilters"
                      :class="clearFilterButtonClass"
                      type="button"
                      @click="clearCategoryFilters"
                    >
                      {{ t("Products.ClearFilters") }}
                    </button>
                  </div>
                  <div
                    class="flex items-center justify-between lg:justify-end gap-3 w-full sm:w-auto"
                  >
                    <Button
                      @click="visible = true"
                      class="!flex lg:!hidden order-1 !py-1.5 !bg-white !border !border-[#E1E1E1] !border-solid"
                      variant="outlined"
                      icon="pi pi-filter"
                      :aria-label="t('Products.Filter')"
                    />
                    <Select
                      v-model="selectedShort"
                      :options="shorts"
                      optionLabel="name"
                      optionValue="code"
                      class="w-52 md:w-60 order-2 bg-white border border-gray-100 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)] rounded-lg md:rounded-xl !text-[13px] font-medium custom-sort-select"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-if="productListPending" :class="productGridClass">
              <UiCardProductSkeleton
                v-for="n in pageSize"
                :key="'product-skeleton-' + n"
                class="w-full"
              />
            </div>
            <div v-else-if="hasProducts" :class="productGridClass">
              <template
                v-for="(item, index) in sortedProductListData"
                :key="item?.documentId || index"
              >
                <UiCardProduct
                  :title="item?.title"
                  :subtitle="getProductSubtitle(item)"
                  :badge="item?.productOverview?.badge ? t('New') : ''"
                  :link="buildProductLink(item)"
                  :media="item?.productOverview?.media"
                  :price="item?.productOverview?.price"
                  :oldPrice="item?.productOverview?.oldPrice"
                  :currency="item?.productOverview?.currency"
                  :position="item?.productOverview?.position"
                  :mediaSize="item?.productOverview?.mediaSize"
                  :objectFit="item?.productOverview?.objectFit"
                  :color="item?.productOverview?.color"
                  :mediaFilled="true"
                  class="w-full bg-white"
                />
              </template>
            </div>
            <div
              v-else
              class="w-full flex flex-col items-center justify-center text-center gap-2 py-16"
            >
              <p class="text-[15px] md:text-[16px] font-semibold text-gray-800">
                {{ emptyStateMessage }}
              </p>
              <p class="text-[13px] md:text-[14px] text-gray-500 font-medium max-w-lg">
                {{ t("Products.EmptyStateDescription") }}
              </p>
            </div>
            <Paginator
              v-if="hasProducts && productListMeta?.pagination?.pageCount > 1"
              :first="(selectedPage - 1) * pageSize"
              :rows="productListMeta?.pagination?.pageSize"
              :totalRecords="productListMeta?.pagination?.total"
              @page="onPageChange"
            />
          </section>
          <!-- Content -->
        </div>
      </div>
    </section>
    <!-- Filter Menu & Content -->

    <SectionsSectionRenderer :sections="productData?.sections" />
  </div>
</template>
<script setup>
import { computed, ref, watch } from "vue";
import qs from "qs";
import Select from "primevue/select";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Paginator from "primevue/paginator";
import { useApi } from "~/server/getApi";
import { useI18n } from "vue-i18n";
import { useLocalePath } from "#i18n";
import { useIsMobile } from "~/composables/utils";
import { useSiteSettingsStore } from "@/store/index";
import { useMetaTags } from "~/composables/headConfig";
import {
  buildJsonLdScripts,
  createBreadcrumbSchema,
  createWebPageSchema,
} from "~/composables/schemaOrg";

const { t, locale } = useI18n();
const { isMobile } = useIsMobile();
const localePath = useLocalePath();
const siteSettingsStore = useSiteSettingsStore();

const defaultCategoryQueryKey = "category";
const categoryQueryKey = computed(() => {
  const translated = t("Routes.CategoryQuery");
  if (typeof translated === "string" && translated.length) {
    return translated !== "Routes.CategoryQuery"
      ? translated
      : defaultCategoryQueryKey;
  }
  return defaultCategoryQueryKey;
});
const categoryQueryKeys = computed(() => {
  const keys = [categoryQueryKey.value];
  if (!keys.includes(defaultCategoryQueryKey)) {
    keys.push(defaultCategoryQueryKey);
  }
  return keys;
});
const props = defineProps({
  forcedCategorySlug: {
    type: String,
    default: null,
  },
  baseProductsPathOverride: {
    type: String,
    default: null,
  },
  categoryPathBuilder: {
    type: Function,
    default: null,
  },
  canonicalPathOverride: {
    type: String,
    default: null,
  },
});
const resolvedForcedCategorySlug = computed(() => {
  const slug = props.forcedCategorySlug;
  return typeof slug === "string" && slug.length ? slug : null;
});
const defaultBaseProductsPath = computed(() => {
  const localizedPath = localePath({ name: "products" });
  return localizedPath || "/products";
});
const baseProductsPath = computed(
  () => props.baseProductsPathOverride || defaultBaseProductsPath.value,
);
const defaultCategoryPathBuilder = (slug) => {
  if (!slug) {
    return baseProductsPath.value;
  }
  const prefix = locale.value === "tr" ? "" : `/${locale.value}`;
  return `${prefix}/${encodeURIComponent(slug)}`;
};
const categoryPathBuilder = computed(
  () => props.categoryPathBuilder || defaultCategoryPathBuilder,
);

const route = useRoute();
const router = useRouter();
const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
const baseUrl = config.public.baseUrl;
const fullUrl = computed(() => `${siteUrl}${route.fullPath}`);
const pageSize = ref(16);
const getPageFromRoute = () => {
  const rawPage = Array.isArray(route.query?.page)
    ? route.query?.page?.[0]
    : route.query?.page;
  const parsedPage = parseInt(rawPage || "", 10);
  return Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
};
const selectedPage = ref(getPageFromRoute());
const isLoading = ref(false);
const visible = ref(false);

const shorts = computed(() => [
  { name: t("Sort.ByCode"), code: "SKU_ASC" },
  { name: t("Sort.ByTitleAsc"), code: "TITLE_ASC" },
  { name: t("Sort.ByTitleDesc"), code: "TITLE_DESC" },
]);
const selectedShort = ref("SKU_ASC");

const sortQuery = computed(() => {
  switch (selectedShort.value) {
    case "TITLE_ASC":
      return ["title:asc"];
    case "TITLE_DESC":
      return ["title:desc"];
    case "SKU_ASC":
    default:
      return ["title:asc"];
  }
});

const getItemSku = (item) => item?.code || item?.sku || item?.productOverview?.code || item?.productOverview?.sku || '';

const sortedProductListData = computed(() => {
  const items = productListData.value || [];
  if (selectedShort.value === 'SKU_ASC') {
    return [...items].sort((a, b) => {
      const skuA = getItemSku(a).toString().toLowerCase();
      const skuB = getItemSku(b).toString().toLowerCase();
      return skuA.localeCompare(skuB, 'tr', { numeric: true });
    });
  }
  return items;
});

const sortKey = computed(() => sortQuery.value.join(","));

const getCategorySlugFromRoute = () => {
  if (resolvedForcedCategorySlug.value) {
    return resolvedForcedCategorySlug.value;
  }
  const paramSlug =
    typeof route.params.categorySlug === "string"
      ? route.params.categorySlug
      : null;
  if (paramSlug) {
    return paramSlug;
  }
  for (const key of categoryQueryKeys.value) {
    const querySlug = route.query?.[key];
    if (Array.isArray(querySlug)) {
      const first = querySlug[0];
      if (typeof first === "string" && first.length) {
        return first;
      }
    } else if (typeof querySlug === "string" && querySlug.length) {
      return querySlug;
    }
  }
  return null;
};

const selectedCategorySlug = ref(getCategorySlugFromRoute());
const canonicalPath = computed(() => {
  if (props.canonicalPathOverride) {
    return props.canonicalPathOverride;
  }
  if (selectedCategorySlug.value) {
    return categoryPathBuilder.value(selectedCategorySlug.value);
  }
  return baseProductsPath.value;
});
const canonicalUrl = computed(() => `${siteUrl}${canonicalPath.value}`);

function buildPaginatedPath(page) {
  const canonical = canonicalPath.value || baseProductsPath.value;
  const [pathOnly, queryString = ""] = canonical.split("?");
  const params = new URLSearchParams(queryString);
  if (page > 1) {
    params.set("page", String(page));
  } else {
    params.delete("page");
  }
  const qs = params.toString();
  return `${pathOnly}${qs ? `?${qs}` : ""}`;
}
watch(
  () => [route.params.categorySlug, route.query, categoryQueryKey.value],
  () => {
    if (resolvedForcedCategorySlug.value) {
      selectedCategorySlug.value = resolvedForcedCategorySlug.value;
      return;
    }
    const slugFromRoute = getCategorySlugFromRoute();
    if (slugFromRoute === selectedCategorySlug.value) {
      return;
    }
    selectedCategorySlug.value = slugFromRoute;
    selectedPage.value = 1;
    if (getPageFromRoute() !== 1) {
      updateRoutePageQuery(1);
    }
  },
);

watch(
  () => resolvedForcedCategorySlug.value,
  (forced) => {
    if (forced) {
      selectedCategorySlug.value = forced;
      selectedPage.value = 1;
      if (getPageFromRoute() !== 1) {
        updateRoutePageQuery(1);
      }
      return;
    }
    selectedCategorySlug.value = getCategorySlugFromRoute();
  },
);

const findCategoryBySlug = (categories = [], slug) => {
  for (const category of categories) {
    if (category?.slug === slug) {
      return category;
    }
    const child = findCategoryBySlug(category?.subcategories || [], slug);
    if (child) {
      return child;
    }
  }
  return null;
};

const collectCategorySlugs = (category) => {
  if (!category) return [];
  const childSlugs = (category.subcategories || []).flatMap((child) =>
    collectCategorySlugs(child),
  );
  return [category.slug, ...childSlugs];
};

const findCategoryPath = (categories = [], slug, path = []) => {
  for (const category of categories) {
    const currentPath = [...path, category];
    if (category?.slug === slug) {
      return currentPath;
    }
    const childPath = findCategoryPath(
      category?.subcategories || [],
      slug,
      currentPath,
    );
    if (childPath.length) {
      return childPath;
    }
  }
  return [];
};

const categoryFilterSlugs = computed(() => {
  if (!selectedCategorySlug.value) {
    return [];
  }
  const categories = productCategoriesData.value || [];
  const match = findCategoryBySlug(categories, selectedCategorySlug.value);
  if (!match) {
    return [selectedCategorySlug.value];
  }
  return Array.from(new Set(collectCategorySlugs(match)));
});

const categoryFilterKey = computed(() => {
  if (!selectedCategorySlug.value) {
    return "__all__";
  }
  return categoryFilterSlugs.value.join(",");
});

const categoryFields = ["title", "slug", "documentId", "id"];

// Menu tree only needs title/slug/id per node. Products are NOT populated
// (the sidebar never lists products), and depth matches the real category
// nesting rather than an arbitrary 5.
const buildSubcategoryPopulateConfig = (depth = 3) => {
  const config = {
    fields: categoryFields,
    sort: ["id:asc"],
  };

  if (depth > 0) {
    config.populate = {
      subcategories: buildSubcategoryPopulateConfig(depth - 1),
    };
  }

  return config;
};

const productCategoriesQuery = computed(() =>
  qs.stringify(
    {
      filters: {
        parent: {
          id: {
            $null: true,
          },
        },
      },
      fields: categoryFields,
      populate: {
        subcategories: buildSubcategoryPopulateConfig(),
      },
      sort: ["id:asc"],
    },
    {
      encodeValuesOnly: true,
    },
  ),
);

const productCategoriesEndpoint = computed(
  () => `product-categories?${productCategoriesQuery.value}`,
);

const {
  data: productCategoriesData,
  fetchData: fetchProductCategoriesData,
  pending: categoriesPending,
} = useApi(productCategoriesEndpoint, true);

// useAsyncData auto-fetches — no need to call fetchData() again

const query = computed(() =>
  qs.stringify(
    {
      // Only what the card renders. Category is used for server-side filtering
      // (filters below) and never displayed, so it is not populated. description
      // (richtext) is not shown in the list — omitted to shrink the payload.
      fields: ["title", "slug", "createdAt", "updatedAt"],
      populate: {
        productOverview: {
          // Real fields on the sections.product component. (badge/position are
          // read by the card but do not exist on the component, so they are not
          // requested — they were always undefined.)
          fields: [
            "sku",
            "price",
            "oldPrice",
            "currency",
            "color",
            "mediaSize",
            "objectFit",
          ],
          populate: { media: true },
        },
      },
      filters: categoryFilterSlugs.value.length
        ? {
            category: {
              slug: {
                $in: categoryFilterSlugs.value,
              },
            },
          }
        : undefined,
      pagination: {
        page: selectedPage.value,
        pageSize: pageSize.value,
      },
      sort: sortQuery.value,
    },
    {
      encodeValuesOnly: true,
    },
  ),
);

const productListEndpoint = computed(() => {
  let base = `product-lists?${query.value}`;
  return base;
});

const { data: productData, fetchData: fetchProductData } =
  useApi(`product-page?pLevel`);

// useAsyncData auto-fetches — no need to call fetchData() again

const selectedCategoryPath = computed(() => {
  const categories = productCategoriesData?.value || [];
  if (!selectedCategorySlug.value) {
    return [];
  }
  return findCategoryPath(categories, selectedCategorySlug.value);
});

const selectedCategory = computed(() => {
  const path = selectedCategoryPath.value;
  if (!path.length) {
    return null;
  }
  return path[path.length - 1] || null;
});

const emptyStateMessage = computed(() => {
  if (selectedCategory.value?.title) {
    return t("Products.EmptyStateCategory", {
      category: selectedCategory.value.title,
    });
  }
  return t("Products.EmptyStateDefault");
});

const heroTitle = computed(
  () =>
    selectedCategory.value?.title ||
    productData.value?.heroDetail?.title ||
    t("Breadcrumb.Products"),
);

const heroDescription = computed(
  () =>
    selectedCategory.value?.description ||
    productData.value?.heroDetail?.description ||
    "",
);

const heroBreadcrumbs = computed(() => {
  const items = [
    {
      enabled: true,
      link: locale.value === "tr" ? "/" : `/${locale.value}`,
      title: homeIconSvg,
    },
  ];

  items.push({
    enabled: true,
    title: t("Breadcrumb.Products"),
    link: baseProductsPath.value,
  });

  selectedCategoryPath.value.forEach((category, index) => {
    if (!category?.title) {
      return;
    }
    const isLast = index === selectedCategoryPath.value.length - 1;
    const crumb = {
      enabled: true,
      title: category.title,
    };
    if (!isLast) {
      crumb.link = categoryPathBuilder.value(category.slug);
    }
    items.push(crumb);
  });

  return items;
});

const {
  data: productListData,
  meta: productListMeta,
  fetchData: fetchProductListData,
  triggerError,
  pending: productListPending,
} = useApi(productListEndpoint, true);

// useAsyncData auto-fetches — no need to call fetchData() again

/* ---------- Computed Sınıflar ---------- */
const filterMenuClass = computed(() => ["hidden lg:flex w-full lg:w-[280px] xl:w-[320px] gap-4 flex-col"]);

const dialogBreakpoints = computed(() => ({ "1199px": "75vw", "575px": "90vw" }));

const clearFilterButtonClass = computed(() => [
  "text-[11px] md:text-[12px] tracking-wider uppercase hover:bg-gray-800 transition-colors cursor-pointer bg-gray-900 text-white font-bold py-1.5 px-3",
  { "rounded-lg md:rounded-xl": true },
]);

const contentClass = computed(() => ["w-full lg:w-[calc(100%-280px)] xl:w-[calc(100%-320px)]"]);

const productGridClass = computed(() => ["grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 2xl:gap-6"]);

// Ensure refetching when filters change
watch(
  () => [selectedShort.value, selectedPage.value, selectedCategorySlug.value],
  async () => {
    await fetchProductListData();
  },
);

const totalProductPages = computed(
  () => productListMeta.value?.pagination?.pageCount ?? 1,
);
const prevPagePath = computed(() =>
  selectedPage.value > 1 ? buildPaginatedPath(selectedPage.value - 1) : null,
);
const nextPagePath = computed(() =>
  selectedPage.value < totalProductPages.value
    ? buildPaginatedPath(selectedPage.value + 1)
    : null,
);
const prevPageUrl = computed(() =>
  prevPagePath.value ? `${siteUrl}${prevPagePath.value}` : null,
);
const nextPageUrl = computed(() =>
  nextPagePath.value ? `${siteUrl}${nextPagePath.value}` : null,
);

const hasProducts = computed(() => (productListData.value || []).length > 0);
const totalProductCount = computed(
  () => productListMeta.value?.pagination?.total ?? 0,
);
const canClearFilters = computed(() => Boolean(selectedCategorySlug.value));

const clearCategoryFilters = () => {
  if (!canClearFilters.value) {
    return;
  }
  router.push(baseProductsPath.value);
  if (isMobile.value) {
    visible.value = false;
  }
};

watch(
  () => route.query.page,
  () => {
    const pageFromRoute = getPageFromRoute();
    if (pageFromRoute === selectedPage.value) {
      return;
    }
    selectedPage.value = pageFromRoute;
  },
);

watch(
  () => [
    categoryFilterKey.value,
    selectedPage.value,
    pageSize.value,
    sortKey.value,
  ],
  (newValues, oldValues) => {
    if (
      oldValues &&
      newValues[0] === oldValues[0] &&
      newValues[1] === oldValues[1] &&
      newValues[2] === oldValues[2] &&
      newValues[3] === oldValues[3]
    ) {
      return;
    }
    fetchProductListData();
  },
);

function buildCategoryLinkPath(slug) {
  if (!slug) {
    return baseProductsPath.value;
  }
  return categoryPathBuilder.value(slug);
}

const handleCategoryNavigation = (slug) => {
  const targetPath = buildCategoryLinkPath(slug);
  const targetBasePath = targetPath.split("?")[0];
  if (route.path === targetBasePath && route.fullPath === targetPath) {
    if (isMobile.value) {
      visible.value = false;
    }
    return;
  }
  router.push(targetPath);
  if (isMobile.value) {
    visible.value = false;
  }
};

const buildProductLink = (item) => {
  const slug = item?.slug;
  if (!slug) {
    return baseProductsPath.value;
  }
  const prefix = locale.value === "tr" ? "" : `/${locale.value}`;
  return `${prefix}/${encodeURIComponent(slug)}`;
};

const formatCategoryLabel = (category) => {
  return category?.title || "";
};

const getCategoryMenuClass = (category) => {
  if (!category?.slug) {
    return undefined;
  }
  return selectedCategorySlug.value === category.slug
    ? "panelmenu-item-active"
    : undefined;
};

const buildPanelMenuItem = (category) => {
  if (!category) return null;

  const childCategories = Array.isArray(category?.subcategories)
    ? sortCategoriesById(category.subcategories)
        .map((child) => buildPanelMenuItem(child))
        .filter((item) => item)
    : [];

  const hasChildren = childCategories.length > 0;

  const menuItem = {
    key: category?.documentId || `category-${category?.id}`,
    label: formatCategoryLabel(category),
    class: getCategoryMenuClass(category),
    ...(hasChildren
      ? {}
      : {
          command: () => handleCategoryNavigation(category?.slug),
        }),
    data: {
      type: "category",
      slug: category?.slug,
      id: category?.id,
    },
  };

  if (hasChildren) {
    menuItem.items = childCategories;
  }

  return menuItem;
};

const sortCategoriesById = (categories) => {
  const desiredOrder = [44, 106, 48]; // PVC (44), Endüstriyel Tutkallar (106), Es Solutions (48)
  return [...categories].sort((a, b) => {
    const indexA = desiredOrder.indexOf(a?.id);
    const indexB = desiredOrder.indexOf(b?.id);
    
    // If both IDs are in our desired list, use that order
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only one is in the list, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    // Fallback to numeric ID sort for others
    return (a?.id ?? 0) - (b?.id ?? 0);
  });
};

const panelMenuItems = computed(() => {
  const categories = sortCategoriesById(productCategoriesData?.value || []);

  return categories
    .map((category) => buildPanelMenuItem(category))
    .filter(Boolean);
});

const panelExpandedKeys = computed(() => {
  if (!selectedCategorySlug.value) {
    return {};
  }
  const categories = productCategoriesData?.value || [];
  const path = findCategoryPath(categories, selectedCategorySlug.value);
  if (!path.length) {
    return {};
  }

  return path.reduce((keys, category) => {
    const key = category?.documentId || `category-${category?.id}`;
    if (key) {
      keys[key] = true;
    }
    return keys;
  }, {});
});

function updateRoutePageQuery(page) {
  const newQuery = { ...route.query };
  if (page > 1) {
    newQuery.page = String(page);
  } else {
    delete newQuery.page;
  }
  router.push({
    path: route.path,
    query: newQuery,
  });
}

function onPageChange(event) {
  const newPage = event.page + 1;
  pageSize.value = event.rows;
  if (getPageFromRoute() === newPage) {
    return;
  }
  updateRoutePageQuery(newPage);
}

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => {
  const seo = productData.value?.seo ?? {};
  return canonicalUrl.value ? { ...seo, canonicalURL: canonicalUrl.value } : seo;
});
const pageTitle = computed(() => heroTitle.value);
const pageDescription = computed(() => heroDescription.value);

useMetaTags(seoData, pageTitle, pageDescription);

/* -------------------- 📤 UseHead Scheme -------------------- */
useHead(() => {
  const page = productData.value;
  if (!page) {
    return { script: [] };
  }

  const scripts = buildJsonLdScripts([
    {
      key: "schema-products-index",
      schema: createWebPageSchema({
        type: "CollectionPage",
        url: canonicalUrl.value,
        name: page.heroDetail?.title || page.title,
        headline: page.heroDetail?.title || page.title,
        description:
          page.seo?.description ||
          page.heroDetail?.description ||
          page.description,
        publishedAt: page.publishedAt,
        updatedAt: page.updatedAt,
        keywords: page.seo?.keywords,
        baseUrl,
        mainEntityType: "CollectionPage",
      }),
    },
    {
      key: "schema-products-breadcrumbs",
      schema: createBreadcrumbSchema(
        heroBreadcrumbs.value.map((b) => ({
          title: b.title.replace(/<[^>]*>?/gm, "").trim() || t("Home"),
          link: b.link,
        })),
        siteUrl,
      ),
    },
  ]);

  return {
    script: scripts,
    link: [
      ...(prevPageUrl.value ? [{ rel: "prev", href: prevPageUrl.value }] : []),
      ...(nextPageUrl.value ? [{ rel: "next", href: nextPageUrl.value }] : []),
    ],
  };
});
const getProductSubtitle = (item) => item?.code || item?.sku || item?.productOverview?.code || item?.productOverview?.sku || '';
const homeIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 md:w-5 md:h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>';
</script>

<style scoped>
/* Override PrimeVue Select to look like a clean pill */
:deep(.custom-sort-select.p-select) {
  border: none !important;
  box-shadow: none !important;
}
:deep(.custom-sort-select.p-select:hover) {
  border: none !important;
}
:deep(.custom-sort-select .p-select-label) {
  color: #4B5563 !important; /* text-gray-600 */
  font-weight: 500;
  font-size: 13px;
  padding: 0.6rem 1rem;
}

/* Clean up Pagination */
/* Override PrimeVue Select Dropdown Panel */
:deep(.p-select-panel) {
  border: none !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
  border-radius: 0.75rem !important;
  padding: 0.5rem !important;
}
:deep(.p-select-option) {
  border-radius: 0.5rem !important;
  color: #4B5563 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 0.75rem 1rem !important;
  margin-bottom: 0.125rem;
}
:deep(.p-select-option.p-focus),
:deep(.p-select-option:hover) {
  background-color: #F9FAFB !important;
  color: #111827 !important;
}
:deep(.p-select-option.p-select-option-selected) {
  background-color: #e5e7eb !important;
  color: #111827 !important;
  font-weight: 600 !important;
}
</style>
